import { defineStore } from 'pinia';
import { collection, addDoc, updateDoc, deleteDoc, doc, getDocs, getDoc, query, where } from 'firebase/firestore';
import type { TeamMember, Invitation } from '~/types/models';
import { PERMISSIONS } from '~/lib/permissions';
import { runFreshRequest, type FreshRequestOptions } from '~/lib/requestFreshness';
import { publicImmutableUploadMetadata } from '~/lib/storageUploadMetadata';
import { getRequiredAuthHeaders } from '~/lib/apiAuth';

export const useTeamStore = defineStore('team', {
    state: () => ({
        members: [] as TeamMember[],
        invitations: [] as Invitation[],
        loading: false,
        error: null as string | null,
        loadedMembersForTenantId: null as string | null,
        loadedInvitationsForTenantId: null as string | null,
    }),

    actions: {
        /**
         * Fetches all team members for current tenant
         */
        async fetchTeamMembers(tenantId: string, options: FreshRequestOptions = {}): Promise<void> {
            const { $db } = useNuxtApp();
            this.error = null;

            try {
                await runFreshRequest(`team-members:${tenantId}`, async () => {
                    this.loading = true;
                    const collectionRef = collection($db, `tenants/${tenantId}/members`);
                    const snap = await getDocs(collectionRef);
                    this.members = snap.docs.map(memberDoc => ({
                        id: memberDoc.id,
                        ...memberDoc.data()
                    } as TeamMember));
                    this.loadedMembersForTenantId = tenantId;
                }, {
                    ...options,
                    hasData: this.loadedMembersForTenantId === tenantId,
                });
            } catch (error) {
                this.error = `Error fetching team members: ${error}`;
                console.error(this.error);
            } finally {
                this.loading = false;
            }
        },

        /**
         * Fetches pending invitations for current tenant
         */
        async fetchInvitations(tenantId: string, options: FreshRequestOptions = {}): Promise<void> {
            const { $db } = useNuxtApp();

            try {
                await runFreshRequest(`team-invitations:${tenantId}`, async () => {
                    const collectionRef = collection($db, 'invitations');
                    const q = query(
                        collectionRef,
                        where('tenantId', '==', tenantId),
                        where('status', '==', 'pending')
                    );
                    const snap = await getDocs(q);
                    this.invitations = snap.docs.map(invitationDoc => ({
                        id: invitationDoc.id,
                        ...invitationDoc.data()
                    } as Invitation));
                    this.loadedInvitationsForTenantId = tenantId;
                }, {
                    ...options,
                    hasData: this.loadedInvitationsForTenantId === tenantId,
                });
            } catch (error) {
                console.error('Error fetching invitations:', error);
            }
        },

        /**
         * Sends an invitation to a new team member
         */
        async inviteTeamMember(
            tenantId: string,
            email: string,
            role: 'admin' | 'member' | 'viewer',
            invitedBy: string
        ): Promise<void> {
            const { $db } = useNuxtApp();
            const tenantStore = useTenantStore();
            if (!PERMISSIONS.canInviteMembers(tenantStore.currentTenantRole)) {
                throw new Error('You do not have permission to invite team members.');
            }

            try {
                // Generate invitation token
                const token = this.generateToken();
                const expiresAt = new Date();
                expiresAt.setDate(expiresAt.getDate() + 7); // Expires in 7 days

                // Create invitation document
                const invitation: Omit<Invitation, 'id'> = {
                    tenantId,
                    email: email.toLowerCase(),
                    role,
                    invitedBy,
                    invitedAt: new Date(),
                    expiresAt,
                    token,
                    status: 'pending',
                };

                const invitationRef = await addDoc(collection($db, 'invitations'), invitation);

                // Create pending team member entry
                const member: Omit<TeamMember, 'id'> = {
                    email: email.toLowerCase(),
                    role,
                    status: 'pending',
                    invitedBy,
                    invitedAt: new Date(),
                };

                const memberRef = await addDoc(collection($db, `tenants/${tenantId}/members`), member);

                this.members.push({ id: memberRef.id, ...member } as TeamMember);
                this.invitations.push({ id: invitationRef.id, ...invitation } as Invitation);

                await this.sendInvitationEmail(email, token);

                console.log('✅ Invitation sent to:', email);
            } catch (error) {
                this.error = `Error inviting team member: ${error}`;
                console.error(this.error);
                throw error;
            }
        },

        /**
         * Validates an invitation token
         */
        async validateInvitation(token: string): Promise<Invitation | null> {
            try {
                const response = await $fetch<{
                    valid: boolean;
                    invitation?: {
                        id: string;
                        tenantId: string;
                        email: string;
                        role: string;
                        expiresAt: string;
                    };
                }>('/api/team-invitations/validate', {
                    query: { token },
                });

                if (!response.valid || !response.invitation) {
                    return null;
                }

                return {
                    id: response.invitation.id,
                    tenantId: response.invitation.tenantId,
                    email: response.invitation.email,
                    role: response.invitation.role as Invitation['role'],
                    invitedBy: '',
                    invitedAt: new Date(),
                    expiresAt: new Date(response.invitation.expiresAt),
                    token,
                    status: 'pending',
                } as Invitation;
            } catch (error) {
                console.error('Error validating invitation:', error);
                return null;
            }
        },

        /**
         * Resends an existing invitation email
         */
        async resendInvitation(invitationId: string): Promise<void> {
            const { $db } = useNuxtApp();

            try {
                const inviteRef = doc($db, 'invitations', invitationId);
                const inviteSnap = await getDoc(inviteRef);

                if (!inviteSnap.exists()) {
                    throw new Error('Invitation not found');
                }

                const invitation = { id: inviteSnap.id, ...inviteSnap.data() } as Invitation;

                if (invitation.status !== 'pending') {
                    throw new Error('Only pending invitations can be resent');
                }

                const expiresAt = new Date(invitation.expiresAt as any);
                if (expiresAt < new Date()) {
                    await this.markInvitationExpired(invitation.id);
                    throw new Error('Invitation has expired');
                }

                await this.sendInvitationEmail(invitation.email, invitation.token);
            } catch (error) {
                this.error = `Error resending invitation: ${error}`;
                console.error(this.error);
                throw error;
            }
        },

        /**
         * Deletes an invitation and removes any matching pending member record
         */
        async deleteInvitation(invitationId: string, tenantId: string, email: string): Promise<void> {
            const { $db } = useNuxtApp();
            const tenantStore = useTenantStore();
            if (!PERMISSIONS.canInviteMembers(tenantStore.currentTenantRole)) {
                throw new Error('You do not have permission to delete invitations.');
            }

            try {
                await deleteDoc(doc($db, 'invitations', invitationId));

                const memberQuery = query(
                    collection($db, `tenants/${tenantId}/members`),
                    where('email', '==', email.toLowerCase()),
                    where('status', '==', 'pending')
                );
                const memberSnap = await getDocs(memberQuery);
                for (const memberDoc of memberSnap.docs) {
                    await deleteDoc(doc($db, `tenants/${tenantId}/members/${memberDoc.id}`));
                }

                this.invitations = this.invitations.filter(invite => invite.id !== invitationId);
                this.members = this.members.filter(member => !(member.email === email && member.status === 'pending'));
            } catch (error) {
                this.error = `Error deleting invitation: ${error}`;
                console.error(this.error);
                throw error;
            }
        },

        /**
         * Accepts an invitation and creates user account
         */
        async acceptInvitation(
            invitationId: string,
            tenantId: string,
            email: string,
            role: TeamMember['role'],
            password: string | undefined,
            displayName: string,
            avatarFile?: File | null,
        ): Promise<void> {
            const { $auth, $storage } = useNuxtApp();

            try {
                const normalizedEmail = email.toLowerCase();
                const currentUser = $auth.currentUser;
                const currentEmail = String(currentUser?.email || '').trim().toLowerCase();
                let authUser = currentUser;

                const { createUserWithEmailAndPassword, updateProfile } = await import('firebase/auth');

                if (authUser) {
                    if (currentEmail !== normalizedEmail) {
                        throw new Error('This invitation belongs to a different email address. Sign in with the invited email to continue.');
                    }
                } else {
                    if (!password) {
                        throw new Error('Please choose a password to create your account.');
                    }

                    const userCredential = await createUserWithEmailAndPassword($auth, normalizedEmail, password);
                    authUser = userCredential.user;
                }

                if (!authUser) {
                    throw new Error('Unable to resolve the invited account.');
                }

                const userId = authUser.uid;
                const resolvedDisplayName = String(displayName || authUser.displayName || normalizedEmail.split('@')[0] || 'Team member').trim();

                let avatarURL = '';
                if (avatarFile) {
                    const { ref: imageRef, uploadBytes, getDownloadURL } = await import('firebase/storage');
                    const filename = avatarFile.name.replace(/[^a-zA-Z0-9.-]/g, '_');
                    const avatarRef = imageRef($storage, `users/${userId}/avatar/${Date.now()}-${filename}`);
                    await uploadBytes(avatarRef, avatarFile, publicImmutableUploadMetadata(avatarFile));
                    avatarURL = await getDownloadURL(avatarRef);
                }

                await updateProfile(authUser, {
                    displayName: resolvedDisplayName,
                    ...(avatarURL ? { photoURL: avatarURL } : {}),
                });

                const idToken = await authUser.getIdToken();
                await $fetch('/api/team-invitations/accept', {
                    method: 'POST',
                    headers: {
                        authorization: `Bearer ${idToken}`,
                    },
                    body: {
                        invitationId,
                        displayName: resolvedDisplayName,
                        avatarURL,
                    },
                });

                if (!authUser.emailVerified) {
                    try {
                        await useAuthStore().sendVerificationEmail({
                            redirectPath: `/${tenantId}/my-hub`,
                        });
                    } catch (verificationError) {
                        console.warn('Unable to send verification email for team invite:', verificationError);
                    }
                }

                const userStore = useUserStore();
                const tenantStore = useTenantStore();
                await userStore.fetchUser(userId);
                await tenantStore.fetchUserTenants(userId);
                if (tenantStore.tenants.some((entry) => entry.tenantId === tenantId)) {
                    await tenantStore.setCurrentTenant(tenantId);
                }

                console.log('✅ Invitation accepted, user created:', userId);
            } catch (error) {
                this.error = `Error accepting invitation: ${error}`;
                console.error(this.error);
                throw error;
            }
        },

        /**
         * Marks invitation as expired
         */
        async markInvitationExpired(invitationId: string): Promise<void> {
            const { $db } = useNuxtApp();

            try {
                await updateDoc(doc($db, 'invitations', invitationId), {
                    status: 'expired',
                });
            } catch (error) {
                console.error('Error marking invitation as expired:', error);
            }
        },

        /**
         * Removes a team member
         */
        async removeTeamMember(tenantId: string, memberId: string): Promise<void> {
            const { $db } = useNuxtApp();
            const tenantStore = useTenantStore();
            if (!PERMISSIONS.canRemoveMembers(tenantStore.currentTenantRole)) {
                throw new Error('You do not have permission to remove team members.');
            }

            try {
                await deleteDoc(doc($db, `tenants/${tenantId}/members/${memberId}`));
                this.members = this.members.filter(m => m.id !== memberId);
            } catch (error) {
                this.error = `Error removing team member: ${error}`;
                console.error(this.error);
                throw error;
            }
        },

        /**
         * Updates a team member's role
         */
        async updateMemberRole(
            tenantId: string,
            memberId: string,
            role: TeamMember['role']
        ): Promise<void> {
            const { $db } = useNuxtApp();
            const tenantStore = useTenantStore();
            if (!PERMISSIONS.canManageRoles(tenantStore.currentTenantRole)) {
                throw new Error('You do not have permission to change member roles.');
            }

            try {
                await updateDoc(doc($db, `tenants/${tenantId}/members/${memberId}`), {
                    role,
                });

                const index = this.members.findIndex(m => m.id === memberId);
                if (index > -1) {
                    const member = this.members[index];
                    if (member) {
                        member.role = role;
                    }
                }
            } catch (error) {
                this.error = `Error updating member role: ${error}`;
                console.error(this.error);
                throw error;
            }
        },

        /**
         * Generates a random invitation token
         */
        generateToken(): string {
            return Math.random().toString(36).substring(2) + Date.now().toString(36);
        },

        /**
         * TODO: Send invitation email
         */
        async sendInvitationEmail(email: string, token: string): Promise<void> {
            const tenantStore = useTenantStore();
            const authStore = useAuthStore();
            const userStore = useUserStore();
            const tenantId = tenantStore.currentTenantId;
            const inviteLink = `${window.location.origin}/accept-invite?token=${token}`;

            await $fetch('/api/send-invite', {
                method: 'POST',
                headers: await getRequiredAuthHeaders(),
                body: {
                    tenantId,
                    to: email,
                    inviteLink,
                    inviterName: authStore.user?.displayName || userStore.user?.displayName || tenantStore.currentTenant?.name || 'Vuey',
                    workspaceName: tenantStore.currentTenant?.name || 'Vuey',
                    template: 'team_invite',
                },
            });
        },
    },

    getters: {
        activeMembers: (state) => state.members.filter(m => m.status === 'active'),
        pendingMembers: (state) => state.members.filter(m => m.status === 'pending'),
        memberCount: (state) => state.members.filter(m => m.status === 'active').length,
    },
});
